import { Toast } from "@/helpers/toast";
import googleDriveLogo from "@/assets/svg/google-drive.svg";
import oneDriveLogo from "@/assets/svg/onedrive.svg";
import dropboxLogo from "@/assets/svg/dropbox.svg";
import boxLogo from "@/assets/svg/box.svg";
import {
  hasDatasetTsValidationError,
  isBackfillConflict,
  parseDatasetTsValidationError,
  readBackfillTaskId,
} from "@/helpers/timezone";

// File extensions the cloud-storage picker may offer, per dataset type.
// Mirrors DATASET_TYPE_EXTENSIONS in core/app/libs/mygpt/dataset_types.py:
// dictionary datasets accept CSV only, time series accepts tabular files, and
// everything else accepts the full DocumentType set.
const CLOUD_ALLOWED_EXTENSIONS = {
  dictionary: ["csv"],
  ts: ["csv", "json", "xlsx"],
  default: ["csv", "json", "xlsx", "docx", "pdf", "pptx", "ppt", "jpg", "jpeg", "png"],
};

// Types whose content is only reachable by describing it.
const OCR_EXTENSIONS = ["pdf", "docx", "jpg", "jpeg", "png"];

// Cloud storage providers offered by the source selector and the file picker.
export const CLOUD_PROVIDERS = [
  {
    value: "onedrive",
    icon: "mdi-microsoft-onedrive",
    logo: oneDriveLogo,
    color: "#0078d4",
    label: "OneDrive",
  },
  {
    value: "googledrive",
    icon: "mdi-google-drive",
    logo: googleDriveLogo,
    color: "#4285f4",
    label: "Google Drive",
  },
  {
    value: "dropbox",
    icon: "mdi-dropbox",
    logo: dropboxLogo,
    color: "#0061FF",
    label: "Dropbox",
  },
  {
    value: "box",
    icon: "mdi-box",
    logo: boxLogo,
    color: "#0061D5",
    label: "Box",
  },
];

export function supportsOcr({ name = "", mime_type = "" } = {}) {
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (OCR_EXTENSIONS.includes(ext)) return true;
  return ["pdf", "image", "wordprocessingml"].some((part) =>
    (mime_type || "").includes(part),
  );
}

export function cloudSelectionSupportsOcr(cloudSources) {
  return (cloudSources || []).some((source) =>
    (source.items || []).some((item) => item.is_folder || supportsOcr(item)),
  );
}

const TEXT_EXTRACTION_EXTENSIONS = ["pdf", "ppt", "pptx", "docx"];

function supportsTextExtraction({ name = "", mime_type = "" } = {}) {
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (TEXT_EXTRACTION_EXTENSIONS.includes(ext)) return true;
  return ["pdf", "presentation", "powerpoint", "wordprocessingml"].some((part) =>
    (mime_type || "").includes(part),
  );
}

export function cloudSelectionSupportsTextExtraction(cloudSources) {
  return (cloudSources || []).some((source) =>
    (source.items || []).some((item) => item.is_folder || supportsTextExtraction(item)),
  );
}

export function cloudSelectionHasMetadataOptions(cloudSources) {
  return cloudSelectionSupportsOcr(cloudSources) || cloudSelectionSupportsTextExtraction(cloudSources);
}

// A folder's contents are unknown, so it leaves OCR to the user rather than billing every pdf inside.
export function cloudSelectionNeedsOcr(cloudSources) {
  return (cloudSources || []).some((source) =>
    (source.items || []).some((item) => !item.is_folder && supportsOcr(item)),
  );
}

// Mirrors cloudSelectionNeedsOcr for text extraction: a folder's contents are unknown, so adding one must not force this on for files that don't need it.
export function cloudSelectionNeedsTextExtraction(cloudSources) {
  return (cloudSources || []).some((source) =>
    (source.items || []).some((item) => !item.is_folder && supportsTextExtraction(item)),
  );
}

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png"];

function isImageItem({ name = "", mime_type = "" } = {}) {
  const ext = name.includes(".") ? name.split(".").pop().toLowerCase() : "";
  if (IMAGE_EXTENSIONS.includes(ext)) return true;
  return (mime_type || "").includes("image");
}

// Mirrors a local file's `type.includes("image")` check, for cloud-picked items.
export function cloudSelectionHasImage(cloudSources) {
  return (cloudSources || []).some((source) =>
    (source.items || []).some((item) => !item.is_folder && isImageItem(item)),
  );
}

// Shared by every "add items" handler: a newly-introduced OCR requirement defaults on only for images (no alternative extraction method);
// PDF/PPT/PPTX/DOCX default to text extraction instead, leaving OCR opt-in. A requirement that already existed keeps whatever the user had manually toggled instead of being silently reset.
export function deriveCloudOcrOnAdd(cloudSourcesBefore, cloudSourcesAfter, currentIsOcr) {
  if (!cloudSelectionNeedsOcr(cloudSourcesAfter)) return false;
  if (cloudSelectionNeedsOcr(cloudSourcesBefore)) return currentIsOcr;
  return cloudSelectionHasImage(cloudSourcesAfter);
}

// Mirrors deriveCloudOcrOnAdd for the text-extraction flag: a newly-introduced requirement defaults on, but one that already existed keeps whatever the user had manually toggled instead of being silently reset.
// Uses cloudSelectionNeedsTextExtraction (folder-excluding), not cloudSelectionSupportsTextExtraction (folder-including) — the latter is for showing the metadata UI eagerly, not for deciding the actual default.
export function deriveCloudTextExtractionOnAdd(cloudSourcesBefore, cloudSourcesAfter, currentIsTextExtraction) {
  if (!cloudSelectionNeedsTextExtraction(cloudSourcesAfter)) return false;
  if (cloudSelectionNeedsTextExtraction(cloudSourcesBefore)) return currentIsTextExtraction;
  return true;
}

// Shared by every "remove items" handler: removal can only drop or keep the OCR requirement, never introduce a new one, so there's no "just became needed" case to default on.
export function deriveCloudOcrOnRemove(cloudSourcesAfter, currentIsOcr) {
  return cloudSelectionNeedsOcr(cloudSourcesAfter) ? (currentIsOcr ?? true) : false;
}

export function cloudAllowedExtensions(datasetType) {
  return (
    CLOUD_ALLOWED_EXTENSIONS[datasetType] || CLOUD_ALLOWED_EXTENSIONS.default
  );
}

export function cloudFolderExtensions(datasetType) {
  return cloudAllowedExtensions(datasetType);
}

export function cloudItemsForProvider(cloudSources, provider) {
  return (cloudSources || [])
    .filter((s) => s.provider === provider)
    .flatMap((s) => s.items || []);
}

export function cloudSelectionHasFolder(cloudSources) {
  return (cloudSources || []).some((s) =>
    (s.items || []).some((i) => i.is_folder),
  );
}

export function mergeCloudSelection(cloudSources, provider, selectedItems) {
  const existing = (cloudSources || []).filter((s) => s.provider !== provider);
  if (!selectedItems.length) return existing;

  const byAccount = new Map();
  selectedItems.forEach((item) => {
    const accountId = item.account_id || "";
    if (!byAccount.has(accountId)) byAccount.set(accountId, []);
    byAccount.get(accountId).push(item);
  });

  const grouped = [...byAccount.entries()].map(([account_id, items]) => ({
    provider,
    account_id,
    items,
  }));
  return [...existing, ...grouped];
}

export function removeCloudItem(cloudSources, provider, itemId) {
  return (cloudSources || [])
    .map((s) =>
      s.provider === provider
        ? { ...s, items: (s.items || []).filter((i) => i.id !== itemId) }
        : s,
    )
    .filter((s) => (s.items || []).length);
}

export function removeCloudProvider(cloudSources, provider) {
  return (cloudSources || []).filter((s) => s.provider !== provider);
}
export function cloudSelectionIdsEqual(before, after) {
  const idsOf = (sources) =>
    (sources || [])
      .flatMap((s) =>
        (s.items || []).map(
          (i) => `${s.provider}:${s.account_id || ""}:${i.id}`,
        ),
      )
      .sort();
  const a = idsOf(before);
  const b = idsOf(after);
  return a.length === b.length && a.every((v, idx) => v === b[idx]);
}

export const datasetMixin = {
  data() {
    return {
      countryCodes: [],
      cloudPickerOpen: false,
    };
  },
  computed: {
    datasetType: {
      get() {
        return this.$store.state.project.datasetType;
      },
      set(value) {
        this.$store.dispatch("project/SET_DATASET_TYPE", value);
      },
    },
    contentSource: {
      get() {
        return this.$store.state.project.contentSource;
      },
      set(value) {
        this.$store.dispatch("project/SET_CONTENT_SOURCE", value);
      },
    },
    typeInfo() {
      return {
        ts: { name: "Time Series", color: "#cc79a7" },
        url: { name: "URL", color: "#0072B2" },
        dictionary: { name: "Dictionary", color: "#d55e00" },
        regular: { name: "Regular", color: "#009E73" },
      };
    },
    cloudFileExtensions() {
      return cloudAllowedExtensions(this.datasetType);
    },
    cloudFolderExtensionOptions() {
      return cloudFolderExtensions(this.datasetType);
    },
    // Consumers of this mixin expose the current dataset as either `dataset`
    // or `selectedGptDataset` - resolve to whichever one is actually present.
    cloudDataset() {
      return this.selectedGptDataset || this.dataset;
    },
    existingCloudItems() {
      return cloudItemsForProvider(
        this.cloudDataset?.cloudSources,
        this.cloudDataset?.cloudProvider,
      );
    },
    hasFolderSelected() {
      return cloudSelectionHasFolder(this.cloudDataset?.cloudSources);
    },
  },
  methods: {
    openCloudPickerFor(provider) {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: { ...this.cloudDataset, cloudProvider: provider },
      });
      this.$emit("open-cloud-picker");
    },
    handleCloudExtensionsChange(val) {
      this.UPDATE_GPT_DATASET_STATE({
        dataset: { ...this.cloudDataset, cloudExtensions: val || [] },
      });
    },
    handleCloudFileSelect(selectedItems) {
      const before = this.cloudDataset.cloudSources;
      const updated = mergeCloudSelection(
        before,
        this.cloudDataset.cloudProvider,
        selectedItems,
      );
      this.UPDATE_GPT_DATASET_STATE({
        dataset: {
          ...this.cloudDataset,
          cloudSources: updated,
          cloudSourcesDirty:
            this.cloudDataset.cloudSourcesDirty ||
            !cloudSelectionIdsEqual(before, updated),
          is_ocr: deriveCloudOcrOnAdd(before, updated, this.cloudDataset.is_ocr),
          is_text_extraction: deriveCloudTextExtractionOnAdd(before, updated, this.cloudDataset.is_text_extraction),
        },
      });
    },
    openCloudPickerForSelection() {
      this.cloudPickerOpen = true;
    },
    handleCloudPickerCancel() {
      this.cloudPickerOpen = false;
    },
    preventInvalidChars(event) {
      const invalidChars = ["e", "E", "+", "-", "."];
      if (invalidChars.includes(event.key)) {
        event.preventDefault();
      }
    },
    getDatasetType(dataset) {
      let type = "regular";
      if (dataset?.dataset_type && dataset.dataset_type === "time_series") {
        type = "ts";
      } else if ((dataset.urls || dataset.social_media_type) && !dataset.tags?.includes("dictionary")) {
        type = "url";
      } else if (!dataset.urls && dataset.tags?.includes("dictionary")) {
        type = "dictionary";
      }
      return type;
    },
    getContentSource(dataset) {
      const hasUrls = dataset.urls && (!Array.isArray(dataset.urls) || dataset.urls.length > 0);
      const isDictionary = dataset.tags?.includes("dictionary");
      const hasSocialMediaType = !!dataset.social_media_type;

      if (!hasUrls && hasSocialMediaType && !isDictionary) {
        return "socialMedia";
      }

      return "webUrl";
    },
    setDatasetType(dataset) {
      const type = this.getDatasetType(dataset);
      this.$store.dispatch("project/SET_DATASET_TYPE", type);
    },
    setContentSource(dataset) {
      const source = this.getContentSource(dataset);
      this.$store.dispatch("project/SET_CONTENT_SOURCE", source);
    },
    checkUrlReachability(err) {
      let nonReachableUrlIndexes = [];
      const errDetail = err?.response?.data?.detail;
      if (Array.isArray(errDetail)) {
        nonReachableUrlIndexes = errDetail?.reduce((acc, errItem) => {
          if (errItem?.msg.includes("Invalid URL")) {
            acc.push(errItem.loc[2]);
          }
          return acc;
        }, []);
      }

      if (nonReachableUrlIndexes?.length > 0) {
        this.$refs.dataset.setUrlErrors(nonReachableUrlIndexes);
        this.isReachabiltyChecking = false;

        const errorMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.reachability_error");

        Toast.error(errorMsg);
      }
    },
    checkUrlDuplicacy(err, urls = []) {
      const errDetailMsg = err?.response?.data?.detail?.[0]?.msg;
      const hasDuplicate = errDetailMsg?.includes("Duplicate URLs");

      if (hasDuplicate) {
        const url = errDetailMsg.split("URLs")[1].trim().slice(7, -1);
        const indexOfUrl = urls.findIndex(item => item === url);
        this.$refs.dataset.setUrlErrors([indexOfUrl], "duplicacy");
        const errorMsg = this.$vuetify.lang.t("$vuetify.gpt_dataset.datasetType.urls.duplicate_error");
        Toast.error(errorMsg);
      }
    },
    // Setting `naive_policy` rewrites values already stored, which otherwise
    // looks like a plain metadata edit.
    notifyTimezoneBackfill(response) {
      const taskId = readBackfillTaskId(response);
      if (!taskId) return null;
      Toast.info(this.$vuetify.lang.t("$vuetify.gpt_dataset.timezone_backfill_started"));
      return taskId;
    },
    // Puts timezone errors on the rows they belong to. True when handled here,
    // so the caller skips its generic toast.
    checkDatasetTsTimezoneErrors(err) {
      if (isBackfillConflict(err)) {
        Toast.error(this.$vuetify.lang.t("$vuetify.gpt_dataset.timezone_backfill_running"));
        return true;
      }
      if (!hasDatasetTsValidationError(err)) return false;

      const parsed = parseDatasetTsValidationError(err);
      const tsComponent = this.$refs?.dataset?.$refs?.datasetTs;
      if (typeof tsComponent?.setTimezoneErrors !== "function") return false;

      tsComponent.setTimezoneErrors(parsed);

      const summary = parsed.form[0] || parsed.master;
      if (summary) {
        Toast.error(summary);
      } else {
        Toast.error(
          this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_details_error")
        );
      }
      return true;
    },
    buildDatasetUpdatePayload(selectedGptDataset) {
      // base payload
      let payload = {
        datasetName: selectedGptDataset.datasetName,
        description: selectedGptDataset.description,
        tags: selectedGptDataset.tags,
      };

      if (this.datasetType === "ts") {
        payload.primary_datetime_field = selectedGptDataset.primary_datetime_field;
        payload.datetime_fields_tz = selectedGptDataset.datetime_fields_tz;
        payload.master_timezone = selectedGptDataset.master_timezone ?? null;
        payload.naive_policy = selectedGptDataset.naive_policy ?? null;
        payload.aware_policy = selectedGptDataset.aware_policy ?? null;
        payload.dataset_type = "time_series";
      } else {
        // fields shared by both 'url' and default
        Object.assign(payload, {
          indexColumnName: selectedGptDataset.indexColumnName,
          imageColumn: selectedGptDataset.imageColumn,
          secondaryIndex: selectedGptDataset.secondaryIndex,
          fillUpEmptyIndex: selectedGptDataset.fillUpEmptyIndex,
          filterableFields: selectedGptDataset.filterableFields,
        });

        if (this.datasetType === "url") {
          // for social media
          if (this.contentSource === "socialMedia") {
            Object.assign(payload, {
              socialMediaType: selectedGptDataset.socialMediaType,
              countryCode: selectedGptDataset.countryCode,
              socialMediaTrendsLimit: selectedGptDataset.socialMediaTrendsLimit,
              socialMediaPostsPerTrend: selectedGptDataset.socialMediaPostsPerTrend,
            });
          } else if (this.contentSource === "webUrl") {
            // for webUrl
            Object.assign(payload, {
              urls: selectedGptDataset.urls,
              scrapingMode: selectedGptDataset.scrapingMode,
              ...(selectedGptDataset.scrapingMode === "nested" && {
                maxDepth: selectedGptDataset.maxDepth || null,
              }),
            });
          }
          // common for webUrl and social Media
          Object.assign(payload, {
            updateFrequency: selectedGptDataset.updateFrequency,
            ...(selectedGptDataset.updateFrequency === "custom" && {
              numOfDays: selectedGptDataset.numOfDays,
            }),
          });
        } else {
          Object.assign(payload, {
            is_ocr: selectedGptDataset.is_ocr,
            is_text_extraction: selectedGptDataset.is_text_extraction,
            metadata: selectedGptDataset.metadata,
          });
        }
      }

      if (this.datasetType !== "url" && selectedGptDataset.uploadSource === "cloud") {
        Object.assign(payload, {
          uploadSource: "cloud",
          cloudUpdateFrequency: selectedGptDataset.cloudUpdateFrequency,
          cloudSyncHours: selectedGptDataset.cloudSyncHours,
          cloudExtensions: selectedGptDataset.cloudExtensions,
        });
      }

      return payload;
    },

    // `isCloudUpload` opts into showing the server's own rejection text.
    // Local uploads are pre-validated in the browser and keep the existing
    // status-code messages.
    getDatasetErrorMessage(error, isCloudUpload = false) {
      const response = error.response;
      const [status, data] = [response?.status, response?.data];
      if (error.message === "Network Error") {
        return this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_network_error");
      }
      const detail = data?.detail;
      if (
        typeof detail?.error === "string" &&
        detail.error.includes("Invalid encoding")
      ) {
        return this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error");
      }
      // The server explains why it rejected the upload (missing datetime
      // field, unusable cloud object, ...). Cloud uploads cannot be
      // pre-validated in the browser, so this is often the only useful
      // detail — show it instead of guessing from the status code.
      if (isCloudUpload) {
        const serverMessage =
          typeof detail === "string"
            ? detail
            : typeof detail?.error === "string"
              ? detail.error
              : typeof detail?.message === "string"
                ? detail.message
                : null;
        if (serverMessage) return serverMessage;
      }
      switch (status) {
        case 400:
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.encoding_error");
        case 401:
        case 403:
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_credentials_error");
        case 404:
        case 422:
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_details_error");
        default:
          return this.$vuetify.lang.t("$vuetify.gpt_dataset.dataset_general_error");
      }
    },
    convertUrl(url) {
      if (!url || typeof url !== "string") return "";
      let normalized = url.trim().toLowerCase();
      if (normalized.endsWith("/")) normalized = normalized.slice(0, -1);
      return normalized;
    },
    isUrlsChanged(prevUrls, newUrls) {
      if (!prevUrls && !newUrls) return false;
      if (!prevUrls || !newUrls) return false;
      if (!Array.isArray(prevUrls) || !Array.isArray(newUrls)) return true;
      if (prevUrls.length !== newUrls.length) return true;

      const prev = [...prevUrls]
        .map(u => this.convertUrl(u))
        .sort()
        .join("|");
      const next = [...newUrls]
        .map(u => this.convertUrl(u))
        .sort()
        .join("|");
      return prev !== next;
    },
    validateUrl(value) {
      if (!value) return true;

      const DOMAIN_PATTERN =
        /^(?!-)[A-Za-z\d]([A-Za-z\d-]{0,61}[A-Za-z\d])?(\.[A-Za-z\d]([A-Za-z\d-]{0,61}[A-Za-z\d])?)*\.[A-Za-z]{2,63}$/;
      const errorMessage = this.$vuetify.lang.t("$vuetify.project.url_validation_error");

      try {
        const url = new URL(value);

        // 1. Validate protocol
        if (!["http:", "https:"].includes(url.protocol)) {
          return errorMessage;
        }

        // 2. Validate port (must be between 1-65535)
        if (url.port) {
          const portNum = parseInt(url.port, 10);
          if (portNum < 1 || portNum > 65535) {
            return errorMessage;
          }
        }

        // 3. Validate hostname
        if (url.hostname !== "localhost") {
          const dotCount = (url.hostname.match(/\./g) || []).length;
          const minDots = url.hostname.startsWith("www.") ? 2 : 1;

          if (dotCount < minDots) {
            return errorMessage;
          }

          if (/\.\./.test(url.hostname) || /^-|-$/.test(url.hostname)) {
            return errorMessage;
          }

          const hasValidTLD = /\.[A-Za-z]{2,63}$/.test(url.hostname);
          if (!hasValidTLD || !DOMAIN_PATTERN.test(url.hostname)) {
            return errorMessage;
          }
        }

        // 4. Validate path - check for unencoded spaces or invalid characters
        if (url.pathname && /[\s<>{}|\\^`]/.test(url.pathname)) {
          return errorMessage;
        }

        // 5. Strict Query Parameter Validation
        if (url.search) {
          const queryString = url.search.slice(1); // Remove '?'

          if (queryString) {
            const params = queryString.split("&");

            for (const param of params) {
              // Must contain '='
              if (!param.includes("=")) {
                return errorMessage;
              }

              const equalIndex = param.indexOf("=");
              const key = param.substring(0, equalIndex);
              const value = param.substring(equalIndex + 1);

              // Key validation
              if (!key) {
                return errorMessage;
              }

              // Key must contain only: A-Za-z0-9_- (no special chars)
              if (!/^[A-Za-z0-9_-]+$/.test(key)) {
                return errorMessage;
              }

              // Value validation - check for unencoded special characters
              // Common characters that should be encoded: space, :, ,, <, >, {, }, |, \, ^, `, [, ]
              if (/[\s:,<>{}|\\^`[\]]/.test(value)) {
                return errorMessage;
              }
            }
          }
        }

        return true;
      } catch {
        return errorMessage;
      }
    },
    getCountryName(code) {
      if (!code || typeof code !== "string") return "";
      const countryNames = new Intl.DisplayNames(["en"], { type: "region" });
      return countryNames.of(code.trim().toUpperCase());
    },
    isBlank(v) {
      return v === null || v === undefined || v === "";
    },
  },
};
